import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { useAuth } from '@hooks/useAuth';
import { useAppSelector } from '@store/hooks';
import AdminProtectedRoute from '@components/common/AdminProtectedRoute';
import AdminLayout from '@components/layouts/AdminLayout';
import LoadingSpinner from '@components/common/LoadingSpinner';

const LoginPage = lazy(() => import('@pages/auth/LoginPage'));
const AdminDashboard = lazy(() => import('@pages/admin'));
const SubscribersPage = lazy(() => import('@pages/admin/SubscribersPage'));
const MonitoringPage = lazy(() => import('@pages/admin/MonitoringPage'));
const AnalyticsPage = lazy(() => import('@pages/admin/AnalyticsPage'));
const SupportPage = lazy(() => import('@pages/admin/SupportPage'));
const AIInsightsPage = lazy(() => import('@pages/admin/AIInsightsPage'));
const ChangelogPage = lazy(() => import('@pages/admin/ChangelogPage'));
const AdminActionsPage = lazy(() => import('@pages/admin/AdminActionsPage'));

const UnauthorizedPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
      <p className="text-gray-500 mb-6">You do not have admin access to this portal.</p>
      <a href="/login" className="text-indigo-600 hover:underline text-sm">
        Sign in with a different account
      </a>
    </div>
  </div>
);

const App = () => {
  useAuth();
  const { isLoading, isAuthenticated } = useAppSelector(state => state.auth);

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        <Route
          path="/"
          element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />}
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* Protected admin routes */}
        <Route
          element={
            <AdminProtectedRoute>
              <AdminLayout />
            </AdminProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<AdminDashboard />} />
          <Route path="/dashboard/subscribers" element={<SubscribersPage />} />
          <Route path="/dashboard/monitoring" element={<MonitoringPage />} />
          <Route path="/dashboard/analytics" element={<AnalyticsPage />} />
          <Route path="/dashboard/support" element={<SupportPage />} />
          <Route path="/dashboard/ai-insights" element={<AIInsightsPage />} />
          <Route path="/dashboard/changelog" element={<ChangelogPage />} />
          <Route path="/dashboard/actions" element={<AdminActionsPage />} />
        </Route>

        {/* Catch all */}
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />}
        />
      </Routes>
    </Suspense>
  );
};

export default App;
